// Pure parser for the inline `__EMAIL_DRAFT_CARD__{json}` marker emitted by
// the email-drafter agent. Lives outside the React component so message-list
// rendering can call it without pulling the EmailDraftCard component chunk
// (which carries lucide icons, useState wiring, and ~13 KB of UI it only
// needs when a draft card actually renders).

import { inlineJsonMarker } from './inlineJsonMarker'

export const EMAIL_DRAFT_CARD_MARKER = '__EMAIL_DRAFT_CARD__'

export type EmailDraftPayload = {
  draft_id: string
  subject: string
  to?: string
  cc?: string
  body_html?: string
  preview?: string
  web_link?: string
  source_message_id?: string
  source_conversation_id?: string
}

const emailDraftCardMarker = inlineJsonMarker<EmailDraftPayload>(EMAIL_DRAFT_CARD_MARKER)

export const parseEmailDraftCard = emailDraftCardMarker.parse
export const splitEmailDraftCard = emailDraftCardMarker.split

/**
 * The Graph `webLink` opens a draft in OWA's READ-ONLY pane
 * (`...?ItemID=<id>&viewmodel=ReadMessageItem`), where Mark can't send or edit.
 * Rewrite it to the compose/edit deep link so "Open in Outlook" lands on an
 * editable, sendable draft. Falls back to the original link only if the id
 * can't be extracted.
 */
export function outlookDraftEditUrl(webLink: string | undefined | null): string {
  if (!webLink) return ''
  try {
    const url = new URL(webLink)
    // The Graph webLink carries the OWA item id but forces the read-only pane.
    // OWA opens a draft *editable* at /mail/0/drafts/id/<id> (the same id it
    // navigates to when you click a draft in the Drafts folder).
    let itemId = url.searchParams.get('ItemID')
    if (!itemId) {
      const match = url.pathname.match(/\/(?:deeplink\/)?read\/([^/?]+)/)
      if (match) itemId = decodeURIComponent(match[1])
    }
    if (!itemId) return webLink
    return `${url.origin}/mail/0/drafts/id/${encodeURIComponent(itemId)}`
  } catch {
    return webLink
  }
}

/** Outlook Android app package — the deep-link target for "Open in Outlook". */
export const OUTLOOK_APP_PACKAGE = 'com.microsoft.office.outlook'

const viteEnv = (import.meta as ImportMeta & {
  env?: Record<string, string | undefined>
}).env ?? {}

/**
 * The mailbox whose folder the deep link opens. The outlook CLI only ever sends
 * as this account, so the link scopes Outlook to it when several accounts are
 * signed in. Device-confirmed (2026-05-31): `folder=drafts&account=…` launches
 * Outlook's mail UI.
 */
export const OUTLOOK_ACCOUNT = (viteEnv.VITE_AGENTCHAT_OUTLOOK_ACCOUNT ?? '').trim()

export type OutlookFolder = 'drafts' | 'sent'

/**
 * Outlook mobile deep link to a mail folder. Outlook exposes NO route to open a
 * specific message/draft in edit mode — verified by decompiling the installed
 * app (`DeepLinkDefs` has only message `open`, a read pane that renders blank
 * for a draft, and `new`, compose-fresh). So "Open in Outlook" lands on the
 * folder instead: the item sits at the top, one tap into Outlook's native UI.
 * `folder=drafts` is the app's own internal token (it builds that exact URL).
 *
 * A plain markdown `ms-outlook://` link only resets the PWA (the WebView never
 * hands a custom scheme to Android); this URL must be fired through the native
 * external-open plugin or wrapped in an intent:// to actually launch the app.
 */
export function outlookFolderUrl(folder: OutlookFolder): string {
  const accountScope = OUTLOOK_ACCOUNT
    ? `&account=${encodeURIComponent(OUTLOOK_ACCOUNT)}`
    : ''
  return `ms-outlook://emails?folder=${folder}${accountScope}`
}

/**
 * Android `intent://` wrapper that forces the Outlook app to handle the folder
 * deep link, with the editable OWA web draft as browser_fallback_url when
 * Outlook isn't installed. Mirrors the Chrome intent helper but targets the
 * Outlook package + scheme.
 */
export function outlookFolderIntentUrl(folder: OutlookFolder, webFallbackUrl: string): string {
  const accountScope = OUTLOOK_ACCOUNT
    ? `&account=${encodeURIComponent(OUTLOOK_ACCOUNT)}`
    : ''
  return `intent://emails?folder=${folder}${accountScope}`
    + `#Intent;scheme=ms-outlook;package=${OUTLOOK_APP_PACKAGE};`
    + `S.browser_fallback_url=${encodeURIComponent(webFallbackUrl)};end`
}
